/**
 * Composables
 */

import { roles } from '@shared/composables/auth/userRoles';

import {
  allowedAdminRoutes,
  userAdminRoutes,
  ticketAdminRoutes,
  contactAdminRoutes,
  declarationAdminRoutes,
  conventionsAdminRoutes,
} from '@core/middleware/adminRoutesByRole';

import {
  allowedRoutesByDefault,
  allowedInitiatorRoutes,
  allowedFirstValidatorRoutes,
  allowedSecondValidatorRoutes,
} from '@core/middleware/globalRoutesByRole';
import { allowedAnalyticsRoutes } from '@modules/analytics/composables/access/useAnalyticsRoutes';

/**
 * Stores
 */

import { useSettingsStore } from '@/stores/settings';

/**
 * Types
 */

import type { ValueOf } from '@shared/types/Helpers';
import type { KeycloakRole, KeycloakUser } from '@core/types/Keycloak';
import type { RoutesList, routeTicketEnum } from '@core/router/routeNames';

import { SettingsType } from '@generatedTypes';

const {
  LEGACY_ADMIN,
  SUPER_ADMIN,
  CONTACT_ADMIN,
  TICKET_ADMIN,
  USER_ADMIN,
  INITIATOR,
  FIRST_VALIDATOR,
  SECOND_VALIDATOR,
  DECLARATION_ADMIN,
  CONVENTIONS_ADMIN,
  ADMIN_DQC,
  ADMIN_DQC_EDITOR,
  ADMIN_DQC_MANAGER,
} = roles;

function isUpsellRoutes(route: RoutesList) {
  return (
    route === 'admin_settings_notifications' ||
    route === 'admin_settings_notification_create' ||
    route === 'admin_settings_notification_edit'
  );
}

function isUpsellAllowed(route: RoutesList) {
  const { getSetting } = useSettingsStore();

  switch (route) {
    case 'admin_settings_notifications':
      return getSetting(SettingsType.UpsellNotificationsCenter).is_active;
    case 'admin_settings_notification_create':
      return getSetting(SettingsType.UpsellNotificationsCenter).is_active;
    case 'admin_settings_notification_edit':
      return getSetting(SettingsType.UpsellNotificationsCenter).is_active;
    default:
      return false;
  }
}

function handleAdminAuth(
  roles: KeycloakRole[],
  route: ValueOf<typeof routeTicketEnum>,
  user: KeycloakUser,
) {
  let allowedRoutes: RoutesList[] = [];

  for (let i = 0; i < roles.length; i++) {
    const role = roles[i];
    switch (role) {
      case LEGACY_ADMIN:
      case SUPER_ADMIN:
        allowedRoutes.push(...allowedAdminRoutes);
        break;
      case CONTACT_ADMIN:
        allowedRoutes.push(...contactAdminRoutes);
        break;
      case DECLARATION_ADMIN:
        allowedRoutes.push(...declarationAdminRoutes);
        break;
      case CONVENTIONS_ADMIN:
        allowedRoutes.push(...conventionsAdminRoutes);
        break;
      case TICKET_ADMIN:
        allowedRoutes.push(...ticketAdminRoutes);
        break;
      case USER_ADMIN:
        allowedRoutes.push(...userAdminRoutes);
        break;
    }
  }

  if (user.email.includes('@ubaq.io')) {
    allowedRoutes.push('admin_settings_upsell');
  }

  return allowedRoutes.includes(route);
}

function handleGlobalAuth(
  roles: KeycloakRole[],
  route: ValueOf<typeof routeTicketEnum>,
) {
  let allowedRoutes: RoutesList[] = [];
  for (let i = 0; i < roles.length; i++) {
    const role = roles[i];
    switch (role) {
      case INITIATOR:
        allowedRoutes.push(...allowedInitiatorRoutes);
        break;
      case FIRST_VALIDATOR:
        allowedRoutes.push(...allowedFirstValidatorRoutes);
        break;
      case SECOND_VALIDATOR:
        allowedRoutes.push(...allowedSecondValidatorRoutes);
        break;
      case ADMIN_DQC:
      case ADMIN_DQC_EDITOR:
      case ADMIN_DQC_MANAGER:
        allowedRoutes.push(...allowedAnalyticsRoutes);
        break;
      default:
        allowedRoutes.push(...allowedRoutesByDefault);
    }
  }

  return allowedRoutes.includes(route);
}

export { handleAdminAuth, handleGlobalAuth, isUpsellRoutes, isUpsellAllowed };
